import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, BedDouble, ShieldCheck, Bell, ClipboardCheck, TrendingUp, Star } from "lucide-react";

const heroImg = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function ForProviders() {
  return (
    <div data-testid="for-providers-page">
      <section className="border-b border-[#D1D5DB] shv-grain">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-4">For Housing Providers</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-[#0A192F] leading-[1.05]">
              Every empty bed is <br /> <span className="text-[#1E3A8A]">lost revenue</span>.
            </h1>
            <p className="mt-6 text-lg text-[#4A5568] max-w-xl">
              Safe Harbor Veterans is the placement and referral network for transitional housing, recovery residences, veteran housing, ILFs, and shelters. Post vacancies in real time, receive vetted referrals, and fill beds faster.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register?role=provider" data-testid="prov-register"><Button className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white px-7 py-6">Register as Provider <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <Link to="/pricing"><Button variant="outline" className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white px-7 py-6">See Pricing</Button></Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={heroImg} alt="Housing facility" className="w-full h-[420px] object-cover rounded-sm border border-[#D1D5DB]" />
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-[#D1D5DB]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
          <div className="max-w-2xl">
            <div className="shv-rule mb-4"></div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">Built for occupancy, not just visibility.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            <Feature icon={<BedDouble />} title="Real-time bed posting" body="Edit availability the moment a bed opens. Vacancies push to matched seekers and case managers instantly." />
            <Feature icon={<ClipboardCheck />} title="Application & referral inbox" body="Accept, deny, and track applicants through Submitted → Under Review → Accepted → Placed → Completed." />
            <Feature icon={<ShieldCheck />} title="Verified Provider badge" body="Earn the trusted badge during admin verification. Verified providers see more referrals." />
            <Feature icon={<Star />} title="Featured placement" body="Preferred providers appear at the top of every search result for their region." />
            <Feature icon={<Bell />} title="Vacancy alerts dashboard" body="See every vacancy you've broadcasted and which case managers received it." />
            <Feature icon={<TrendingUp />} title="Occupancy analytics" body="Live occupancy rate, placement velocity, and revenue impact." />
          </div>
        </div>
      </section>

      <section className="bg-[#0A192F] text-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">Why providers pay for Safe Harbor</h2>
          <p className="mt-3 text-white/70 max-w-2xl mx-auto">Traffic doesn't fill beds — qualified, verified referrals do. We sell occupancy, not advertising.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 mt-12">
            <Pillar value="$0" label="Cost per empty bed (we help you fix that)" />
            <Pillar value="< 5 min" label="Time to post a new vacancy" />
            <Pillar value="6 stages" label="Tracked from referral to completed placement" />
          </div>
          <Link to="/register?role=provider" className="inline-block mt-12">
            <Button className="bg-[#C05621] hover:bg-[#9C4221] text-white px-8 py-6">Get Listed <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

const Feature = ({ icon, title, body }) => (
  <div>
    <div className="w-10 h-10 rounded-sm bg-[#F5F5F0] border border-[#D1D5DB] flex items-center justify-center text-[#1E3A8A] mb-4">
      {React.cloneElement(icon, { className: "w-5 h-5" })}
    </div>
    <div className="font-heading font-semibold text-[#0A192F] text-lg">{title}</div>
    <div className="text-sm text-[#4A5568] mt-1.5">{body}</div>
  </div>
);

const Pillar = ({ value, label }) => (
  <div className="bg-[#0A192F] p-8 text-center">
    <div className="font-heading text-4xl font-bold text-[#C05621]">{value}</div>
    <div className="text-sm text-white/70 mt-2">{label}</div>
  </div>
);
